import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Bot, Loader2, CheckCircle } from 'lucide-react';
import { skillsAssessmentService, ChatMessage, AssessmentResponse, AssistantMessage, AssessmentResult } from '../../services/skillsAssessment';
import { doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../services/firebase/config';
import { formatMarkdown } from '../../utils/markdownFormatter';
import { useAuth } from '../../contexts/AuthContext';

// Fallback map to recover keys if the AI returns a slightly different key or no key
const TEXT_TO_KEY_MAP: Record<string, string> = {
  'experience level': 'experience_level',
  'tried any ai tools': 'has_tried_ai_tools',
  'type of ai tools': 'ai_tool_category',
  'achieve with ai': 'ai_goals',
  'another language': 'needs_language_support',
  'dedicate to learning': 'weekly_time_commitment',
  'industry do you work': 'industry',
  'industry': 'industry_role',
  'role': 'industry_role',
  'skillsets': 'role_skillsets',
  'project': 'personal_project_intent',
  'expectations': 'specific_learning_expectations',
  'ready to start': 'ready_to_start',
  'summary': 'assessment_summary'
};

interface SkillsAssessmentChatProps {
  onAssessmentComplete?: (assessmentId: string, extractedData?: AssessmentResult['extractedData']) => void;
  className?: string;
}

const SkillsAssessmentChat: React.FC<SkillsAssessmentChatProps> = ({
  onAssessmentComplete,
  className = ''
}) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isAssessmentComplete, setIsAssessmentComplete] = useState(false);
  const [multiSelectSelections, setMultiSelectSelections] = useState<Record<string, string[]>>({});
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const buildMessageId = useCallback((message: ChatMessage, fallbackIndex: number) => {
    let timestampValue: string | undefined;

    if (message.timestamp) {
      if ('seconds' in message.timestamp && 'nanoseconds' in message.timestamp) {
        // Firestore Timestamp
        timestampValue = `${message.timestamp.seconds}-${message.timestamp.nanoseconds}`;
      } else if (message.timestamp instanceof Date) {
        // JS Date object
        timestampValue = message.timestamp.getTime().toString();
      } else if (typeof message.timestamp === 'string' || typeof message.timestamp === 'number') {
        // ISO string or timestamp number
        timestampValue = message.timestamp.toString();
      }
    }

    // Use timestamp as the primary unique identifier if available
    // Fallback to index-based ID if no timestamp
    const uniquePart = timestampValue || `idx-${fallbackIndex}`;
    
    const keyPart = message.key ? `-${message.key}` : '';
    const textPreview = (message.text || message.content || '').slice(0, 20).replace(/[^a-zA-Z0-9]/g, '');
    
    return `${message.role}${keyPart}-${uniquePart}-${textPreview}`;
  }, []);

  const applyMessageDefaults = useCallback((message: ChatMessage, index: number): ChatMessage => {
    // Try to recover key from text if missing or invalid
    let effectiveKey = message.key;
    if (!effectiveKey && message.role === 'assistant' && message.text) {
      const lowerText = message.text.toLowerCase();
      for (const [textSnippet, key] of Object.entries(TEXT_TO_KEY_MAP)) {
        if (lowerText.includes(textSnippet)) {
          effectiveKey = key;
          break;
        }
      }
    }

    // Use options directly from the message
    const effectiveOptions = message.options || [];

    return {
      ...message,
      key: effectiveKey || message.key,
      text: message.text || message.content,
      content: message.content || message.text || '',
      options: effectiveOptions,
      inputType: message.inputType || 'free_text',
      id: message.id || buildMessageId(message, index)
    };
  }, [buildMessageId]);

  const dedupeMessages = useCallback((history: ChatMessage[]): ChatMessage[] => {
    const seen = new Set<string>();
    const normalized = history.map(applyMessageDefaults);

    return normalized.filter((msg, index) => {
      const id = msg.id || buildMessageId(msg, index);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [applyMessageDefaults, buildMessageId]);

  // Get user initials from display name or email
  const getUserInitials = () => {
    if (!user) return 'U';
    
    if (user.displayName) {
      const names = user.displayName.trim().split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return names[0].substring(0, 2).toUpperCase();
    }
    
    if (user.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    
    return 'U';
  };

  const scrollToBottom = () => {
    // Use container scroll instead of scrollIntoView to avoid page scroll
    // Use setTimeout to ensure DOM has fully updated
    setTimeout(() => {
      if (messagesContainerRef.current) {
        // Use instant scroll to prevent page scroll propagation
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 0);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Prevent scroll events from bubbling to parent
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = (e: Event) => {
      e.stopPropagation();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const hydrateHistory = useCallback((
    history: ChatMessage[] = [],
    lastAssistantMessage?: AssistantMessage
  ): ChatMessage[] => {
    const normalizedHistory = (history || []).map(applyMessageDefaults);

    if (lastAssistantMessage && normalizedHistory.length > 0) {
      const lastIndex = normalizedHistory.length - 1;
      const lastMessage = normalizedHistory[lastIndex];
      if (lastMessage.role === 'assistant') {
        normalizedHistory[lastIndex] = applyMessageDefaults({
          ...lastMessage,
          text: lastAssistantMessage.text,
          content: lastAssistantMessage.text,
          options: lastAssistantMessage.options,
          inputType: lastAssistantMessage.inputType,
          key: lastAssistantMessage.key,
        }, lastIndex);
      }
    }

    if ((!normalizedHistory || normalizedHistory.length === 0) && lastAssistantMessage) {
      return dedupeMessages([applyMessageDefaults({
        role: 'assistant',
        content: lastAssistantMessage.text,
        text: lastAssistantMessage.text,
        options: lastAssistantMessage.options,
        inputType: lastAssistantMessage.inputType,
        key: lastAssistantMessage.key,
      }, 0)]);
    }

    return dedupeMessages(normalizedHistory);
  }, [applyMessageDefaults, dedupeMessages]);

  const startAssessment = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await skillsAssessmentService.startAssessment();
      const hydrated = hydrateHistory(response.conversationHistory, response.lastAssistantMessage);
      setMessages(hydrated);
    } catch (error: any) {
      console.error('Error starting assessment:', error);
      const code = error?.code || error?.message || '';
      let friendlyMessage: string;

      if (code.includes('resource-exhausted') || code.includes('Too many')) {
        friendlyMessage =
          "You've reached the maximum number of assessments for today. " +
          "Please come back tomorrow — we'll be here!";
      } else {
        friendlyMessage =
          "We're having trouble connecting right now. Please try refreshing the page, or contact support if this persists.";
      }

      setMessages(dedupeMessages([{ role: 'assistant', content: friendlyMessage }]));
    } finally {
      setIsLoading(false);
    }
  }, [dedupeMessages, hydrateHistory]);

  useEffect(() => {
    // Start as soon as there is any Firebase auth session — real or anonymous.
    // We use auth.currentUser directly rather than the context `user` because
    // anonymous sessions intentionally have user=null in the app context.
    if (auth.currentUser) {
      startAssessment();
    }
  }, [startAssessment]);

  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || currentMessage;
    const trimmedMessage = textToSend.trim();
    if (!trimmedMessage || isLoading || isSending || isAssessmentComplete) return;

    setIsSending(true);

    // Filter out error messages and failed user messages from history before sending
    // This prevents the "I apologize..." loop and ensures the backend gets a clean history
    const historyBeforeSend = dedupeMessages(messages).filter(msg => 
      !msg.content.includes('I apologize, but I encountered an error') &&
      !msg.content.includes('having trouble connecting')
    );

    const userChatMessage: ChatMessage = applyMessageDefaults({
      role: 'user',
      content: trimmedMessage,
      timestamp: new Date()
    }, historyBeforeSend.length);

    // Immediately add user message to chat
    setMessages(prev => dedupeMessages([...prev, userChatMessage]));

    // Clear input and set loading
    if (!messageText) {
      setCurrentMessage('');
    }
    setIsLoading(true);

    try {
      const response: AssessmentResponse = await skillsAssessmentService.sendChatMessage(
        trimmedMessage,
        historyBeforeSend
      );

      const hydratedHistory = hydrateHistory(response.conversationHistory, response.lastAssistantMessage);
      setMessages(hydratedHistory);
      
      if (response.isAssessmentComplete) {
        setIsAssessmentComplete(true);
        // Process the assessment
        const result = await skillsAssessmentService.processAssessment();
        
        // Update user document to mark assessment as completed
        const user = auth.currentUser;
        if (user) {
          try {
            await updateDoc(doc(db, 'users', user.uid), {
              skillsAssessmentCompleted: true,
              skillsAssessmentCompletedAt: new Date(),
            });
          } catch (error) {
            console.error('Error updating user assessment completion status:', error);
          }
        }
        
        onAssessmentComplete?.(result.assessmentId, result.extractedData);
      }
    } catch (error: any) {
      console.error('Error sending message:', error);

      // Surface a human-readable message for known error codes
      let friendlyMessage: string;
      const code = error?.code || error?.message || '';

      if (code.includes('resource-exhausted') || code.includes('Too many')) {
        friendlyMessage =
          "You've reached the maximum number of assessments for today. " +
          "Please come back tomorrow to continue — your progress so far is saved.";
      } else if (code.includes('unauthenticated')) {
        friendlyMessage =
          'Your session has expired. Please refresh the page to continue.';
      } else if (code.includes('unavailable') || code.includes('deadline-exceeded')) {
        friendlyMessage =
          "Our servers are experiencing high demand right now. Please wait a moment and try again.";
      } else {
        friendlyMessage =
          'Sorry, something went wrong processing your message. Please try again.';
      }

      setMessages(prev => dedupeMessages([...prev, {
        role: 'assistant',
        content: friendlyMessage,
      }]));
    } finally {
      setIsLoading(false);
      setIsSending(false);
      // Refocus the input after sending - use requestAnimationFrame for better mobile support
      requestAnimationFrame(() => {
        if (inputRef.current && !isAssessmentComplete) {
          inputRef.current.focus();
          // On iOS, also trigger click to ensure keyboard stays open
          if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
            inputRef.current.click();
          }
        }
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
      e.stopPropagation();
    }
  };

  const toggleMultiSelectOption = (key: string | undefined, option: string) => {
    if (!key || isLoading || isSending || isAssessmentComplete) return;

    setMultiSelectSelections((prev) => {
      const current = prev[key] || [];
      const exists = current.includes(option);
      const updated = exists ? current.filter(item => item !== option) : [...current, option];
      return {
        ...prev,
        [key]: updated,
      };
    });
  };

  const submitMultiSelectSelection = (key?: string) => {
    if (!key) return;
    const selections = multiSelectSelections[key] || [];
    if (selections.length === 0) return;

    sendMessage(selections.join(', '));
    setMultiSelectSelections((prev) => ({
      ...prev,
      [key]: [],
    }));
  };

  const isBusy = isLoading || isSending;

  return (
    <div className={`flex flex-col bg-white rounded-lg shadow-lg scroll-mt-20 min-h-0 ${className}`}>
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-teal-50 rounded-t-lg">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">AI Skills Assessment</h3>
            <p className="text-sm text-gray-600">
              {isAssessmentComplete ? 'Assessment Complete!' : 'Let\'s discover your AI skills'}
            </p>
          </div>
        </div>
        {isAssessmentComplete && (
          <CheckCircle className="w-6 h-6 text-green-500" />
        )}
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-4">
        {messages.map((message, index) => {
          const timestampValue =
            message.timestamp?.seconds !== undefined && message.timestamp?.nanoseconds !== undefined
              ? `${message.timestamp.seconds}-${message.timestamp.nanoseconds}`
              : typeof message.timestamp === 'string' || typeof message.timestamp === 'number'
                ? message.timestamp.toString()
                : undefined;
          const messageKey = message.id ?? (timestampValue ?? `${message.role}-${index}-${message.content}`);
          const displayText = message.text || message.content;
          const isMultiSelect = message.inputType === 'multi_select';
          const selectedOptions = message.key ? multiSelectSelections[message.key] || [] : [];
          // Disable interaction if global loading state is true OR if this is not the last message
          const isLastMessage = index === messages.length - 1;
          const isInteractionDisabled = isLoading || isSending || isAssessmentComplete || (!isLastMessage && message.role === 'assistant');
          
          return (
          <div
            key={messageKey}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`flex items-start space-x-2 max-w-[80%] ${
                message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.role === 'user'
                    ? 'bg-blue-500'
                    : 'bg-gradient-to-r from-teal-500 to-blue-500'
                }`}
              >
                {message.role === 'user' ? (
                  <span className="text-xs font-semibold text-white">{getUserInitials()}</span>
                ) : (
                  <Bot className="w-4 h-4 text-white" />
                )}
              </div>
              <div
                className={`px-4 py-2 rounded-lg ${
                  message.role === 'user'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                {message.role === 'assistant' ? (
                  <div className="flex flex-col gap-2">
                    {formatMarkdown(displayText || '')}
                    
                    {/* Helper text for input types */}
                    {message.inputType === 'multi_select' && (
                      <p className="text-xs text-gray-500 italic mt-1">Select one or more options below, then click Submit. If none match, you can type your answer.</p>
                    )}
                    {message.inputType === 'single_select' && message.options && message.options.length > 0 && (
                      <p className="text-xs text-gray-500 italic mt-1">Select one option below. If none match, you can type your answer.</p>
                    )}
                    {message.inputType === 'free_text' && (!message.options || message.options.length === 0) && (
                      <p className="text-xs text-gray-500 italic mt-1">Please type your answer below.</p>
                    )}
                    {message.inputType === 'free_text' && message.options && message.options.length > 0 && (
                      <p className="text-xs text-gray-500 italic mt-1">Select a suggestion or type your own answer.</p>
                    )}

                    {message.options && message.options.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {message.options.map((option, idx) => (
                          <button
                            key={`${message.key || 'option'}-${idx}`}
                            onClick={() => isMultiSelect ? toggleMultiSelectOption(message.key, option) : sendMessage(option)}
                            disabled={isInteractionDisabled}
                            className={`px-3 py-1.5 border rounded-full text-sm transition-all duration-200 text-left shadow-sm ${
                              isMultiSelect && selectedOptions.includes(option) 
                                ? 'bg-blue-100 border-blue-500 text-blue-800 font-medium ring-2 ring-blue-200' 
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600'
                            } ${isInteractionDisabled ? 'opacity-50 cursor-not-allowed hover:bg-white hover:border-gray-300 hover:text-gray-700' : ''}`}
                          >
                            {option}
                          </button>
                        ))}
                        {isMultiSelect && (
                          <button
                            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-full text-sm font-medium shadow-sm hover:from-blue-600 hover:to-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => submitMultiSelectSelection(message.key)}
                            disabled={isInteractionDisabled || selectedOptions.length === 0}
                          >
                            Submit selection
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm leading-relaxed text-white whitespace-pre-wrap">{message.content}</div>
                )}
              </div>
            </div>
          </div>
        );
      })}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="flex items-start space-x-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-500 to-blue-500 flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="bg-gray-100 px-4 py-2 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                  <span className="text-sm text-gray-500">Thinking...</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      {!isAssessmentComplete && (
        <div className="flex-shrink-0 p-4 border-t border-gray-200">
          <div className="flex space-x-2">
            <textarea
              ref={inputRef}
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your response here..."
              className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={1}
              disabled={isBusy}
            />
            <button
              onClick={(e) => {
                e.preventDefault();
                sendMessage();
              }}
              onMouseDown={(e) => {
                // Prevent button from taking focus away from input on mobile
                e.preventDefault();
              }}
              disabled={!currentMessage.trim() || isBusy}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-lg hover:from-blue-600 hover:to-teal-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      )}

      {/* Assessment Complete Message */}
      {isAssessmentComplete && (
        <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-green-50">
          <div className="flex items-center space-x-2 text-green-700">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Assessment Complete!</span>
          </div>
          <p className="text-sm text-green-600 mt-1">
            Your skills have been analyzed. We're now generating your personalized learning plan.
          </p>
        </div>
      )}
    </div>
  );
};

export default SkillsAssessmentChat;

